/**
 * The deck.
 *
 * Composes the plinth, platter, record, tonearm, controls and progress ring
 * into one object with a small imperative API. Everything here is in
 * centimetres, in the deck's own frame; the maths behind it lives in
 * `src/geometry`, which knows nothing about Three.js.
 */

import * as THREE from 'three';
import { DIM } from '../geometry/dims.ts';
import { YAW_END, YAW_LEAD, YAW_REST } from '../geometry/tonearm.ts';
import { pitchFromZ } from '../geometry/transport.ts';
import type { Palette } from '../lib/palette.ts';
import { createArmControl } from './arm-control.ts';
import { createDeckState, integrateArm, integrateSpin, strobeOpacity } from './deck-state.ts';
import { ACCENT, buildMaterials } from './materials.ts';
import { createMotor } from './motor.ts';
import { buildControls, buildFader } from './parts/controls.ts';
import { HitRegistry, type HitType } from './parts/hits.ts';
import { buildLegends } from './parts/legends.ts';
import { buildPlatter } from './parts/platter.ts';
import { buildPlinth } from './parts/plinth.ts';
import { buildTonearm } from './parts/tonearm.ts';
import { buildProgressRing } from './progress-ring.ts';
import type { RecordMesh } from './record.ts';
import { hitVolume } from './shapes.ts';
import { createSwapper } from './swap.ts';

export type { SwapOptions } from './swap.ts';
export type Turntable = ReturnType<typeof buildTurntable>;

export function buildTurntable(renderer: THREE.WebGLRenderer) {
  const M = buildMaterials(renderer);
  const root = new THREE.Group();
  const hits = new HitRegistry();
  const state = createDeckState();

  buildPlinth(root, M);
  const platterGroup = buildPlatter(root, M);
  const ring = buildProgressRing(root);
  const arm = buildTonearm(root, M, hits.add);
  const leds = buildControls(root, M, hits.add);
  const faderKnob = buildFader(root, M, hits.add);
  buildLegends(root, M);

  const armControl = createArmControl(state, arm, ring);
  const motor = createMotor(state, {
    leds,
    faderKnob,
    ring,
    onPowerOff: () => {
      armControl.setArmDown(false);
    },
  });
  const records = createSwapper(renderer, root, state, armControl);
  const tipWorld = new THREE.Vector3();

  /* A flat disc just above the record catches scratch gestures. */
  const discHit = hitVolume(new THREE.CircleGeometry(DIM.recordR, 48));
  discHit.rotation.x = -Math.PI / 2;
  discHit.position.set(DIM.platter.x, DIM.recordTop + 0.02, DIM.platter.z);
  root.add(hits.add(discHit, { type: 'disc' }));

  function update(dt: number): void {
    integrateSpin(state, dt);
    platterGroup.rotation.y = state.angle;
    records.disc.spinner.rotation.y = state.angle;
    /* The outgoing record keeps turning, a little slower, as it slides away. */
    if (records.outgoing) records.outgoing.spinner.rotation.y = state.angle * 0.7;

    integrateArm(state, dt);
    arm.pivot.rotation.y = state.armYaw;

    leds.strobe.opacity = strobeOpacity(state, dt);
    /* The progress ring breathes very slightly with the audio level. */
    ring.setOpacity(0.78 + state.level * 0.22);
  }

  motor.setPowered(true);
  motor.setRPM(33);
  motor.setPitch(0);
  motor.setSpinning(false);

  return {
    root,
    state,
    update,
    platterGroup,
    armPivot: arm.pivot,
    progressRing: ring,
    swapRecord: records.swap,
    setPowered: motor.setPowered,
    setRPM: motor.setRPM,
    setPitch: motor.setPitch,
    setSpinning: motor.setSpinning,
    setArmDown: armControl.setArmDown,
    setProgress: armControl.setProgress,
    dragArmTo: armControl.dragArmTo,
    dropNeedle: armControl.dropNeedle,
    liftNeedle: armControl.liftNeedle,
    parkArm: armControl.park,
    restArm: armControl.restArm,
    cueArm: armControl.cue,

    get hits(): THREE.Mesh[] {
      return hits.meshes;
    },
    get disc(): RecordMesh {
      return records.disc;
    },
    get spinning(): boolean {
      return state.targetSpin > 0;
    },
    get platterAngle(): number {
      return state.angle;
    },

    findHit: (type: HitType, value?: number) => hits.find(type, value),
    /** Where the stylus tip is right now, in deck space — the point that meets the groove. */
    stylusTip: (): [number, number, number] => arm.stylus.getWorldPosition(tipWorld).toArray(),
    pitchFromPoint: (p: THREE.Vector3) => motor.setPitch(pitchFromZ(p.z)),
    setAccent: (hex: string) => {
      ring.setColor(hex || ACCENT);
    },
    setLevel: (v: number) => {
      state.level = v;
    },
    setPictureDisc: (on: boolean, image: CanvasImageSource | null, palette: Palette | null) => {
      records.disc.setArtwork(image, palette, on);
    },
    nudgeAngle: (d: number) => {
      state.angle += d;
    },
    YAW_REST,
    YAW_LEAD,
    YAW_END,
  };
}
